import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, ChevronDown, Contact, Hash, Headphones, ListFilter, Lock, MessageSquareText, Pencil, Plus, Search, Send, SlidersHorizontal, SquarePen, Star, MessageCircle, LayoutGrid } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, Presence } from "./Avatar";
import { NotificationBanner } from "./NotificationBanner";
import { HealthPill } from "./HealthPill";
import { useAuth } from "@/lib/auth";
import { useChat } from "@/lib/chat";
import { useIsMobile } from "@/lib/device";

const Section = ({ icon: Icon, label, children, onAdd, addLabel, testid }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="tc-sidebar__group">
      <div className="tc-sb-section" role="button" tabIndex={0} onClick={() => setOpen((o) => !o)} data-testid={`sidebar-section-${testid}`}>
        <span className="tc-sb-section__icon"><Icon /></span>
        <span className="tc-sb-section__label">{label}</span>
        {onAdd && <span className="tc-sb-section__plus" title={addLabel} onClick={(e) => { e.stopPropagation(); onAdd(); }} data-testid={`sidebar-section-${testid}-add`}><Plus size={14} /></span>}
        <ChevronDown size={14} className={`tc-sb-section__caret${open ? "" : " is-collapsed"}`} />
      </div>
      {open && children}
    </div>
  );
};

export const ConversationItem = ({ c, active, onClick, indent = true }) => {
  const unread = (c.unread_count || 0) > 0;
  const isDm = c.type !== "channel";
  const other = isDm && c.type === "dm" ? c.members_info?.[0] : null;
  return (
    <button className={`tc-sb-item${active ? " is-active" : ""}${unread ? " is-unread" : ""}${indent ? " is-indent" : ""}`} onClick={onClick} data-testid={`sidebar-conversation-${c.id}`} title={c.display_name}>
      {!isDm ? (
        <span className="tc-sb-item__icon">{c.is_private ? <Lock /> : <Hash />}</span>
      ) : c.type === "dm" ? (
        <span className="tc-dm-avatar"><Avatar user={other} size={20} radius={4} /><Presence presence={other?.presence} /></span>
      ) : (
        <span className="tc-mpdm-count">{c.member_count - 1}</span>
      )}
      <span className="tc-sb-item__label">{c.display_name}</span>
      <span className="tc-sb-item__meta">
        {c.draft && !active && <Pencil size={13} data-testid="draft-indicator" />}
        {c.type === "channel" ? (c.mention_count > 0 && <span className="tc-badge" data-testid={`badge-${c.id}`}>{c.mention_count}</span>) : (c.unread_count > 0 && <span className="tc-badge" data-testid={`badge-${c.id}`}>{c.unread_count}</span>)}
      </span>
    </button>
  );
};

export const Sidebar = ({ view, sub }) => {
  const navigate = useNavigate();
  const { workspace } = useAuth();
  const { conversations, currentId, setModal, me, users, can } = useChat();
  const [filter, setFilter] = useState("");
  const isMobile = useIsMobile();

  const channels = useMemo(() => conversations.filter((c) => c.type === "channel").sort((a, b) => a.name.localeCompare(b.name)), [conversations]);
  const dms = useMemo(() => conversations.filter((c) => c.type !== "channel").sort((a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0)), [conversations]);
  const starred = conversations.filter((c) => c.is_starred);
  const draftsCount = conversations.filter((c) => c.draft).length;
  const bot = users.find((u) => u.is_system_bot);
  const q = filter.trim().toLowerCase();
  const match = (c) => !q || (c.display_name || "").toLowerCase().includes(q);
  const go = (c) => { localStorage.setItem("tc_last_conv", c.id); navigate(`/client/${c.id}`); };

  const NavItem = ({ id, icon: Icon, label, meta, bold }) => (
    <button className={`tc-sb-item${view === id ? " is-active" : ""}${bold ? " is-unread" : ""}`} onClick={() => navigate(`/client/${id}`)} data-testid={`sidebar-nav-${id}`}>
      <span className="tc-sb-item__icon"><Icon /></span>
      <span className="tc-sb-item__label">{label}</span>
      {meta}
    </button>
  );

  return (
    <aside className="tc-sidebar" data-testid="workspace-sidebar">
      <div className="tc-sidebar__header">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="tc-sidebar__ws" data-testid="workspace-menu-button"><span>{workspace?.name}</span><ChevronDown size={16} strokeWidth={2.5} /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="tc-menu" style={{ minWidth: 280 }}>
            <div style={{ padding: "6px 24px 10px", fontSize: 13, color: "var(--tc-text-muted)" }}>{workspace?.app_name} · {workspace?.email_domain}</div>
            {can("members.invite") && <DropdownMenuItem className="tc-menu__item" onClick={() => navigate("/admin/users?invite=1")} data-testid="menu-invite-people">Invite people to {workspace?.name}</DropdownMenuItem>}
            {can("channels.create") && <DropdownMenuItem className="tc-menu__item" onClick={() => setModal({ type: "createChannel" })}>Create a channel</DropdownMenuItem>}
            <DropdownMenuItem className="tc-menu__item" onClick={() => setModal({ type: "browseChannels" })}>Browse channels</DropdownMenuItem>
            <DropdownMenuItem className="tc-menu__item" onClick={() => setModal({ type: "preferences" })} data-testid="workspace-menu-preferences">Preferences</DropdownMenuItem>
            {can("admin.console") && (<><DropdownMenuSeparator className="tc-menu__sep" /><DropdownMenuItem className="tc-menu__item" onClick={() => navigate("/admin")}>Tools & settings</DropdownMenuItem></>)}
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="tc-sidebar__hbtns">
          {isMobile && <HealthPill />}
          {isMobile && <button className="tc-iconbtn" title="Search" onClick={() => setModal({ type: "search" })} data-testid="sidebar-search-button"><Search size={18} /></button>}
          <button className="tc-iconbtn" title="Filter conversations" onClick={() => setModal({ type: "browseChannels" })} data-testid="sidebar-filter-button"><SlidersHorizontal size={18} /></button>
          {can("dm.send") && <button className="tc-iconbtn" title="New message" onClick={() => setModal({ type: "newMessage" })} data-testid="sidebar-compose-button"><SquarePen size={18} /></button>}
        </div>
      </div>
      <label className="tc-sidebar__find">
        <ListFilter size={15} />
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Find a conversation..." data-testid="sidebar-find-input" />
      </label>
      <NotificationBanner />
      <div className="tc-sidebar__list tc-scroll">
        <NavItem id="threads" icon={MessageSquareText} label="Threads" bold={false} />
        <NavItem id="huddles" icon={Headphones} label="Huddles" />
        <NavItem id="drafts" icon={Send} label="Drafts & sent" meta={draftsCount ? <span className="tc-sb-item__meta"><Pencil size={13} /> {draftsCount}</span> : null} />
        <NavItem id="directories" icon={Contact} label="Directories" />
        <div className="tc-sidebar__divider" />
        {starred.length > 0 && (
          <Section icon={Star} label="Starred" testid="starred">
            {starred.filter(match).map((c) => <ConversationItem key={c.id} c={c} active={currentId === c.id && view === "home"} onClick={() => go(c)} />)}
          </Section>
        )}
        <Section icon={LayoutGrid} label="Channels" testid="channels" onAdd={can("channels.create") ? () => setModal({ type: "createChannel" }) : undefined} addLabel="Create channel">
          {channels.filter(match).filter((c) => !c.is_starred).map((c) => <ConversationItem key={c.id} c={c} active={currentId === c.id && view === "home"} onClick={() => go(c)} />)}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="tc-sb-item is-indent tc-sb-add" data-testid="sidebar-add-channels"><span className="tc-sb-item__icon"><Plus /></span><span className="tc-sb-item__label">Add channels</span></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="tc-menu">
              {can("channels.create") && <DropdownMenuItem className="tc-menu__item" onClick={() => setModal({ type: "createChannel" })} data-testid="add-channels-create">Create a new channel</DropdownMenuItem>}
              <DropdownMenuItem className="tc-menu__item" onClick={() => setModal({ type: "browseChannels" })} data-testid="add-channels-browse">Browse channels</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </Section>
        <Section icon={MessageCircle} label="Direct messages" testid="dms" onAdd={can("dm.send") ? () => setModal({ type: "newMessage" }) : undefined} addLabel="New message">
          {dms.filter(match).filter((c) => !c.is_starred).map((c) => <ConversationItem key={c.id} c={c} active={currentId === c.id && view === "home"} onClick={() => go(c)} />)}
          {can("dm.send") && <button className="tc-sb-item is-indent tc-sb-add" onClick={() => setModal({ type: "newMessage" })} data-testid="sidebar-add-teammates"><span className="tc-sb-item__icon"><Plus /></span><span className="tc-sb-item__label">Add teammates</span></button>}
        </Section>
        <Section icon={Bot} label="Agents & apps" testid="apps">
          <button className="tc-sb-item is-indent" onClick={() => navigate("/client/tools")} data-testid="sidebar-app-totalchat">
            <span className="tc-dm-avatar"><Avatar user={bot || { display_name: "Total Chat" }} size={20} radius={4} /><Presence presence="active" /></span>
            <span className="tc-sb-item__label">Total Chat</span>
          </button>
        </Section>
      </div>
    </aside>
  );
};
